import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { authApi, legalDocumentsApi } from '../../../assets/api';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import { useHapticPress } from '../../../assets/hooks/useHapticPress';
import { getPostLoginPath } from '../../../assets/services/loginFlow';
import MiniPreloader from '../../MiniPreloader/MiniPreloader';
import ScreenHeader from '../../ScreenHeader/ScreenHeader';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import LegalDocumentPopup from './LegalDocumentPopup';
import './OnboardingTerms.css';

const TickIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      fill="currentColor"
      d="M8.37 18.275a1 1 0 0 0 1.337-.068l10.5-10.5a1 1 0 1 0-1.414-1.414L9 16.086l-3.293-3.293a1 1 0 1 0-1.414 1.414l4 4z"
    />
  </svg>
);

const DocIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    {...props}
  >
    <path
      fill="currentColor"
      d="M15 16a1 1 0 1 1 0 2H9a1 1 0 1 1 0-2zm0-4a1 1 0 1 1 0 2H9a1 1 0 1 1 0-2zm5 7a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h6a1 1 0 0 1 .707.293l6 6A1 1 0 0 1 20 9zM16.586 8 14 5.414V8zM6 19a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9h-4a2 2 0 0 1-2-2V4H7a1 1 0 0 0-1 1z"
    />
  </svg>
);

function getLocalizedValue(value, locale = 'en') {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'object') {
    return String(value);
  }

  const localizedValue = value[locale] ?? value.en;

  if (typeof localizedValue === 'string') {
    return localizedValue;
  }

  const firstString = Object.values(value).find((item) => typeof item === 'string');

  return firstString || '';
}

function normalizeActiveDocumentsResponse(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
}

function getDocumentKey(document) {
  return document?.slug || document?._id || '';
}

function getDocumentTitle(document, locale, fallbackTitle) {
  return getLocalizedValue(document?.title, locale) || document?.slug || fallbackTitle;
}

function buildSignedDocuments(documents) {
  return documents
    .filter((document) => document?.slug && Number.isInteger(document?.version))
    .map(({ slug, version }) => ({ slug, version }));
}

function stringifyApiErrorValue(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => stringifyApiErrorValue(item))
      .filter(Boolean)
      .join('\n');
  }

  if (typeof value === 'object') {
    return (
      stringifyApiErrorValue(value.msg)
      || stringifyApiErrorValue(value.message)
      || stringifyApiErrorValue(value.detail)
    );
  }

  return String(value);
}

function getApiErrorMessage(error, fallback) {
  return (
    stringifyApiErrorValue(error?.detail)
    || stringifyApiErrorValue(error?.payload?.detail)
    || stringifyApiErrorValue(error?.payload)
    || stringifyApiErrorValue(error?.message)
    || fallback
  );
}

function OnboardingTerms({
  authService = authApi,
  legalDocumentsService = legalDocumentsApi,
}) {
  const navigate = useNavigate();
  const { webApp } = useTelegram();
  const hapticProps = useHapticPress(webApp);
  const { setCurrentUser } = useContext(CurrentUserContext);
  const { language, common, documents: documentsTranslation, t } = useContext(TranslationContext);
  const [documents, setDocuments] = useState([]);
  const [signedBySlug, setSignedBySlug] = useState({});
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(true);
  const [documentsError, setDocumentsError] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [exactDocumentsBySlug, setExactDocumentsBySlug] = useState({});
  const [isExactDocumentLoading, setIsExactDocumentLoading] = useState(false);
  const [exactDocumentError, setExactDocumentError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const signedDocuments = useMemo(() => buildSignedDocuments(documents), [documents]);
  const allChecked = documents.length > 0
    && signedDocuments.length === documents.length
    && documents.every((document) => signedBySlug[document.slug]);
  const canSubmit = allChecked && !isSubmitting && !isDocumentsLoading;
  const selectedSlug = selectedDocument?.slug;
  const exactDocument = selectedSlug
    ? exactDocumentsBySlug[selectedSlug]
    : null;
  const popupDocument = exactDocument || selectedDocument;

  const loadDocuments = useCallback(async () => {
    setIsDocumentsLoading(true);
    setDocumentsError('');

    try {
      const response = await legalDocumentsService.getActive();
      const nextDocuments = normalizeActiveDocumentsResponse(response);

      setDocuments(nextDocuments);
      setSignedBySlug((prevSignedBySlug) => (
        Object.fromEntries(
          nextDocuments
            .filter((document) => document?.slug)
            .map((document) => [document.slug, Boolean(prevSignedBySlug[document.slug])])
        )
      ));
    } catch (error) {
      const message = getApiErrorMessage(error, documentsTranslation.loadDocumentsFailed);

      setDocumentsError(message);
      toast.error(message);
    } finally {
      setIsDocumentsLoading(false);
    }
  }, [documentsTranslation.loadDocumentsFailed, legalDocumentsService]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const loadExactDocument = useCallback(async (document) => {
    if (!document?.slug || exactDocumentsBySlug[document.slug]) {
      return;
    }

    setIsExactDocumentLoading(true);
    setExactDocumentError('');

    try {
      const nextDocument = await legalDocumentsService.getExact({ slug: document.slug });

      setExactDocumentsBySlug((prevDocuments) => ({
        ...prevDocuments,
        [document.slug]: nextDocument,
      }));
    } catch (error) {
      const message = getApiErrorMessage(error, documentsTranslation.loadDocumentFailed);

      setExactDocumentError(message);
      toast.error(message);
    } finally {
      setIsExactDocumentLoading(false);
    }
  }, [documentsTranslation.loadDocumentFailed, exactDocumentsBySlug, legalDocumentsService]);

  const openDocument = (document) => {
    setSelectedDocument(document);
    setExactDocumentError('');
    loadExactDocument(document);
  };

  const closeDocument = () => {
    setSelectedDocument(null);
    setExactDocumentError('');
  };

  const toggleDocument = (document) => {
    if (!document?.slug) {
      return;
    }

    setSignedBySlug((prevSignedBySlug) => ({
      ...prevSignedBySlug,
      [document.slug]: !prevSignedBySlug[document.slug],
    }));
  };

  const handleDocumentKeyDown = (event, document) => {
    hapticProps.onKeyDown?.(event);

    if (event.repeat || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    toggleDocument(document);
  };

  const handleReadClick = (event, document) => {
    event.preventDefault();
    event.stopPropagation();
    openDocument(document);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      const nextCurrentUser = await authService.updateSignedDocuments({
        signedDocuments,
      });

      setCurrentUser(nextCurrentUser);
      navigate(getPostLoginPath(nextCurrentUser), { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error, documentsTranslation.submitFailed));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="onboarding-terms">
      <ScreenHeader title={documentsTranslation.termsTitle} onBack={() => navigate('/onboarding')} />

      <div className="onboarding-terms__content">
        <div className="onboarding-terms__illustration" aria-hidden="true">
          <DocIcon className="onboarding-terms__illustration-icon" />
        </div>

        <h2 className="onboarding-terms__title">{documentsTranslation.submitTitle}</h2>
        <p className="onboarding-terms__subtitle">
          {documentsTranslation.submitText}
        </p>

        {isDocumentsLoading ? (
          <MiniPreloader
            className="onboarding-terms__loader"
            label={documentsTranslation.loadingDocuments}
          />
        ) : documentsError ? (
          <div className="onboarding-terms__empty" role="alert">
            <p>{documentsError}</p>
            <button
              type="button"
              className="onboarding-terms__retry"
              onClick={loadDocuments}
              {...hapticProps}
            >
              {common.tryAgain}
            </button>
          </div>
        ) : documents.length === 0 ? (
          <div className="onboarding-terms__empty">
            {documentsTranslation.noDocuments}
          </div>
        ) : (
          <ul className="onboarding-terms__list">
            {documents.map((document) => {
              const key = getDocumentKey(document);
              const isSigned = Boolean(signedBySlug[document.slug]);
              const title = getDocumentTitle(
                document,
                language,
                documentsTranslation.legalDocument
              );

              return (
                <li key={key} className="onboarding-terms__item">
                  <div
                    className={`onboarding-terms__row${
                      isSigned ? ' onboarding-terms__row--checked' : ''
                    }`}
                    role="button"
                    tabIndex={0}
                    aria-label={t('documents.agreeToDocument', { title })}
                    aria-pressed={isSigned}
                    onClick={() => toggleDocument(document)}
                    {...hapticProps}
                    onKeyDown={(event) => handleDocumentKeyDown(event, document)}
                  >
                    <span className="onboarding-terms__copy">
                      <span className="onboarding-terms__document-title">{title}</span>
                      <span className="onboarding-terms__document-actions">
                        <button
                          type="button"
                          className="onboarding-terms__read"
                          onClick={(event) => handleReadClick(event, document)}
                          {...hapticProps}
                        >
                          {common.read}
                        </button>
                      </span>
                    </span>
                    <span
                      className={`onboarding-terms__checkbox${
                        isSigned ? ' onboarding-terms__checkbox--checked' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {isSigned && <TickIcon className="onboarding-terms__check" />}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <BottomActionButton
        className="onboarding-terms__cta"
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        {isSubmitting ? documentsTranslation.submitting : documentsTranslation.submitDocuments}
      </BottomActionButton>

      <AnimatePresence>
        {selectedDocument && (
          <LegalDocumentPopup
            key={selectedDocument.slug || selectedDocument._id || 'document'}
            document={popupDocument}
            error={exactDocumentError}
            isLoading={isExactDocumentLoading}
            locale={language}
            onClose={closeDocument}
            onRetry={() => loadExactDocument(selectedDocument)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

export default OnboardingTerms;
